import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Alert,
  IconButton as MUIIconButton,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper
} from '@mui/material';
import { Plus, CheckCircle, Timer, Dumbbell, MapPin, ChevronDown, Trash2, Calendar, Edit, Save, X } from 'lucide-react';
import { ExerciseEntry, GymExercise, DatabaseSchema, WorkoutTemplate } from '../types';
import { format } from 'date-fns';
import { WeekNavigation } from './WeekNavigation';
import { getWeekDays } from '../utils/weekUtils';
import { useToast } from './shared/ToastProvider';

interface ExerciseTrackerProps {
  database: DatabaseSchema | null;
  onAddExercise: (exercise: ExerciseEntry) => Promise<void>;
  onDeleteExercise?: (id: string) => Promise<void>;
  updateWorkoutTemplates?: (templates: WorkoutTemplate[]) => Promise<void>;
}

const DEFAULT_WORKOUT_TEMPLATES: WorkoutTemplate[] = [
  {
    id: 'upper-body-day1',
    name: 'Gym Day 1 – Upper Body (Push + Pull)',
    type: 'upper',
    warmup: '5–7 min brisk walk or elliptical',
    duration: 35,
    exercises: [
      { name: 'Machine Row', sets: 3, reps: '12' },
      { name: 'Lat Pulldown', sets: 3, reps: '10-12' },
      { name: 'Hammerstrength Chest Press', sets: 3, reps: '10-12' },
      { name: 'Machine Shoulder Press', sets: 3, reps: '10-12' },
      { name: 'Dumbbell Row', sets: 3, reps: '12 each side' },
      { name: 'Side Lateral Raise', sets: 3, reps: '12-15' },
      { name: 'Tricep Extension', sets: 3, reps: '12' },
      { name: 'Biceps Curl to Shoulder Press', sets: 3, reps: '12' }
    ]
  },
  {
    id: 'lower-body-day2',
    name: 'Gym Day 2 – Lower Body & Core',
    type: 'lower',
    warmup: '5–7 min rowing machine',
    duration: 35,
    exercises: [
      { name: 'Machine Leg Press', sets: 3, reps: '12' },
      { name: 'Leg Extension', sets: 3, reps: '12' },
      { name: 'Seated Leg Curl', sets: 3, reps: '12' },
      { name: 'Dumbbell Lunge', sets: 3, reps: '12 each leg' },
      { name: 'Machine Hip Adductor', sets: 3, reps: '12-15' },
      { name: 'Machine Hip Abductor', sets: 3, reps: '12-15' },
      { name: 'Weighted Plank Hold', sets: 3, reps: '20-40 sec' },
      { name: 'Russian Twist', sets: 3, reps: '20 twists' }
    ]
  }
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`exercise-tabpanel-${index}`}
      aria-labelledby={`exercise-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const ExerciseTracker: React.FC<ExerciseTrackerProps> = ({ 
  database, 
  onAddExercise, 
  onDeleteExercise,
  updateWorkoutTemplates 
}) => {
  const toast = useToast();
  const [exerciseType, setExerciseType] = useState<'walk' | 'gym-upper' | 'gym-lower' | 'cycling'>('walk');
  const [cyclingDistance, setCyclingDistance] = useState('');
  const [isCommute, setIsCommute] = useState(false);
  const [duration, setDuration] = useState('');
  const [notes, setNotes] = useState('');
  const [gymExercises, setGymExercises] = useState<GymExercise[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newExerciseName, setNewExerciseName] = useState('');
  const [newExerciseSets, setNewExerciseSets] = useState('');
  const [newExerciseReps, setNewExerciseReps] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [, setSelectedTemplate] = useState<WorkoutTemplate | null>(null);
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [editingDay, setEditingDay] = useState<{dateString: string, type: string} | null>(null);
  const [editDuration, setEditDuration] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editExercises, setEditExercises] = useState<GymExercise[]>([]);
  const [quickAddLoading, setQuickAddLoading] = useState(false);
  const [workoutTemplates, setWorkoutTemplates] = useState<WorkoutTemplate[]>(DEFAULT_WORKOUT_TEMPLATES);
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
  const [editingTemplateData, setEditingTemplateData] = useState<WorkoutTemplate | null>(null);

  const exercises = database?.exercises || [];
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const todayExercises = exercises.filter(e => e.date === todayStr);
  const recentExercises = [...exercises]
    .filter(e => e.date !== todayStr)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 10);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!duration) return;

    setLoading(true);
    
    try {
      const newExercise: ExerciseEntry = {
        id: `exercise_${Date.now()}`,
        date: todayStr,
        type: exerciseType,
        duration: parseInt(duration),
        completed: false,
        exercises: (exerciseType !== 'walk' && exerciseType !== 'cycling') ? gymExercises : undefined,
        notes: notes || undefined,
        distance: exerciseType === 'cycling' && cyclingDistance ? parseFloat(cyclingDistance) : undefined,
        isCommute: exerciseType === 'cycling' ? isCommute : undefined,
      };

      await onAddExercise(newExercise);

      setDuration('');
      setNotes('');
      setGymExercises([]);
      setCyclingDistance('');
      setIsCommute(false);
      toast.success('Exercise logged successfully');
    } catch (error) {
      console.error('Error adding exercise:', error);
      toast.error('Failed to log exercise');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCompleted = async (exercise: ExerciseEntry) => {
    try {
      await onAddExercise({
        ...exercise,
        completed: !exercise.completed
      });
    } catch (error) {
      console.error('Error updating exercise:', error);
    }
  };

  const handleToggleGymExerciseCompleted = async (exercise: ExerciseEntry, exerciseIndex: number) => {
    try {
      const updatedExercises = [...(exercise.exercises || [])];
      updatedExercises[exerciseIndex] = {
        ...updatedExercises[exerciseIndex],
        completed: !updatedExercises[exerciseIndex].completed
      };

      await onAddExercise({
        ...exercise,
        exercises: updatedExercises
      });
    } catch (error) {
      console.error('Error updating gym exercise:', error);
    }
  };

  const addGymExercise = () => {
    if (!newExerciseName || !newExerciseSets || !newExerciseReps) return;

    const newExercise: GymExercise = {
      name: newExerciseName,
      sets: parseInt(newExerciseSets),
      reps: newExerciseReps,
      completed: false
    };

    setGymExercises([...gymExercises, newExercise]);
    setNewExerciseName('');
    setNewExerciseSets('');
    setNewExerciseReps('');
    setDialogOpen(false);
  };

  const removeGymExercise = (index: number) => {
    setGymExercises(gymExercises.filter((_, i) => i !== index));
  };

  const getExerciseTypeColor = (type: string): string => {
    switch (type) {
      case 'walk': return '#22c55e';
      case 'gym-upper': return '#ef4444';
      case 'gym-lower': return '#f59e0b';
      case 'cycling': return '#6366f1';
      default: return '#4f46e5';
    }
  };

  const getExerciseTypeLabel = (type: string) => {
    switch (type) {
      case 'walk': return 'Walk';
      case 'gym-upper': return 'Gym - Upper Body';
      case 'gym-lower': return 'Gym - Lower Body';
      case 'cycling': return 'Cycling';
      default: return type;
    }
  };

  const getExerciseStats = () => {
    const stats: Record<string, { total: number; completed: number; duration: number }> = {
      walk: { total: 0, completed: 0, duration: 0 },
      'gym-upper': { total: 0, completed: 0, duration: 0 },
      'gym-lower': { total: 0, completed: 0, duration: 0 },
      cycling: { total: 0, completed: 0, duration: 0 },
    };

    todayExercises.forEach(exercise => {
      if (stats[exercise.type]) {
        stats[exercise.type].total++;
        stats[exercise.type].duration += exercise.duration;
        if (exercise.completed) {
          stats[exercise.type].completed++;
        }
      }
    });

    return stats;
  };

  const stats = getExerciseStats();
  const totalDuration = Object.values(stats).reduce((sum, stat) => sum + stat.duration, 0);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleUseTemplate = (template: WorkoutTemplate) => {
    setSelectedTemplate(template);
    setExerciseType(template.type === 'upper' ? 'gym-upper' : 'gym-lower');
    setDuration(template.duration.toString());
    setNotes(`${template.warmup}`);
    const gymExercises: GymExercise[] = template.exercises.map(ex => ({
      name: ex.name,
      sets: ex.sets,
      reps: ex.reps,
      completed: false
    }));
    setGymExercises(gymExercises);
    setCurrentTab(0); // Switch to manual entry tab
  };

  const getTodaysRecommendation = () => {
    const today = new Date().getDay(); // 0 = Sunday, 1 = Monday, etc.
    if (today === 2) { // Tuesday
      return workoutTemplates[0]; // Upper body
    } else if (today === 4) { // Thursday
      return workoutTemplates[1]; // Lower body
    }
    return null;
  };

  const recommendation = getTodaysRecommendation();
  
  // Load templates from database or localStorage on component mount
  React.useEffect(() => {
    if (database?.workoutTemplates) {
      // Load from database if available
      setWorkoutTemplates(database.workoutTemplates);
    } else {
      // Fallback to localStorage
      const savedTemplates = localStorage.getItem('workout_templates');
      if (savedTemplates) {
        try {
          setWorkoutTemplates(JSON.parse(savedTemplates));
        } catch (error) {
          console.error('Failed to load workout templates:', error);
          setWorkoutTemplates(DEFAULT_WORKOUT_TEMPLATES);
        }
      }
    }
  }, [database?.workoutTemplates]);
  
  // Save templates to database or localStorage
  const saveTemplatesToStorage = async (templates: WorkoutTemplate[]) => {
    if (updateWorkoutTemplates) {
      // Save to database if function is available
      await updateWorkoutTemplates(templates);
    } else {
      // Fallback to localStorage
      localStorage.setItem('workout_templates', JSON.stringify(templates));
    }
    setWorkoutTemplates(templates);
  };
  
  const handleEditTemplate = (template: WorkoutTemplate) => {
    setEditingTemplate(template.id);
    setEditingTemplateData({ ...template });
  };
  
  const handleSaveTemplate = async () => {
    if (!editingTemplateData) return;
    
    const updatedTemplates = workoutTemplates.map(template => 
      template.id === editingTemplateData.id ? editingTemplateData : template
    );
    
    await saveTemplatesToStorage(updatedTemplates);
    setEditingTemplate(null);
    setEditingTemplateData(null);
  };
  
  const handleCancelEditTemplate = () => {
    setEditingTemplate(null);
    setEditingTemplateData(null);
  };
  
  const updateTemplateField = (field: keyof WorkoutTemplate, value: any) => {
    if (!editingTemplateData) return;
    setEditingTemplateData({ ...editingTemplateData, [field]: value });
  };
  
  const addExerciseToTemplate = () => {
    if (!editingTemplateData) return;
    const newExercise = { name: 'New Exercise', sets: 3, reps: '10-12' };
    updateTemplateField('exercises', [...editingTemplateData.exercises, newExercise]);
  };
  
  const removeExerciseFromTemplate = (index: number) => {
    if (!editingTemplateData) return;
    const updatedExercises = editingTemplateData.exercises.filter((_, i) => i !== index);
    updateTemplateField('exercises', updatedExercises);
  };
  
  const updateTemplateExercise = (index: number, field: string, value: any) => {
    if (!editingTemplateData) return;
    const updatedExercises = editingTemplateData.exercises.map((exercise, i) => 
      i === index ? { ...exercise, [field]: value } : exercise
    );
    updateTemplateField('exercises', updatedExercises);
  };
  
  const resetTemplatesToDefault = async () => {
    await saveTemplatesToStorage(DEFAULT_WORKOUT_TEMPLATES);
    setEditingTemplate(null);
    setEditingTemplateData(null);
  };
  
  const handleWeekChange = (week: Date) => {
    setSelectedWeek(week);
  };
  
  const getWeeklyExercises = () => {
    const weekDays = getWeekDays(selectedWeek);
    return weekDays.map(day => {
      const dayExercises = exercises.filter(e => e.date === day.dateString);
      return {
        ...day,
        exercises: dayExercises
      };
    });
  };
  
  const weeklyExerciseData = getWeeklyExercises();
  
  const handleEditExercise = (dateString: string, type: string) => {
    const dayExercises = exercises.filter(e => e.date === dateString && e.type === type);
    const existingExercise = dayExercises[0];
    
    setEditingDay({ dateString, type });
    setEditDuration(existingExercise?.duration?.toString() || '');
    setEditNotes(existingExercise?.notes || '');
    setEditExercises(existingExercise?.exercises || []);
  };
  
  const handleSaveExercise = async () => {
    if (!editDuration || !editingDay) return;
    
    const dateObj = new Date(editingDay.dateString);
    if (isNaN(dateObj.getTime())) {
      console.error('Invalid date:', editingDay.dateString);
      return;
    }
    
    setQuickAddLoading(true);
    
    try {
      const newExercise: ExerciseEntry = {
        id: `exercise_${Date.now()}`,
        date: editingDay.dateString,
        type: editingDay.type as any,
        duration: parseInt(editDuration),
        completed: false,
        exercises: editingDay.type !== 'walk' ? editExercises : undefined,
        notes: editNotes || undefined,
      };

      await onAddExercise(newExercise);
      setEditingDay(null);
      setEditDuration('');
      setEditNotes('');
      setEditExercises([]);
      toast.success('Exercise saved successfully');
    } catch (error) {
      console.error('Error saving exercise:', error);
      toast.error('Failed to save exercise');
    } finally {
      setQuickAddLoading(false);
    }
  };
  
  const handleCancelEdit = () => {
    setEditingDay(null);
    setEditDuration('');
    setEditNotes('');
    setEditExercises([]);
  };

  const handleDeleteExercise = async (exercise: ExerciseEntry) => {
    if (!onDeleteExercise) return;
    
    const confirmDelete = window.confirm(`Delete ${getExerciseTypeLabel(exercise.type)} exercise from ${format(new Date(exercise.date + 'T12:00:00'), 'MMM dd, yyyy')}?\n\nDuration: ${exercise.duration} minutes`);
    if (confirmDelete) {
      await onDeleteExercise(exercise.id);
    }
  };

  const handleAddEditExercise = () => {
    if (!newExerciseName || !newExerciseSets || !newExerciseReps) return;

    const newExercise: GymExercise = {
      name: newExerciseName,
      sets: parseInt(newExerciseSets),
      reps: newExerciseReps,
      completed: false
    };

    setEditExercises([...editExercises, newExercise]);
    setNewExerciseName('');
    setNewExerciseSets('');
    setNewExerciseReps('');
    setDialogOpen(false);
  };

  return (
    <Box p={{ xs: 1, sm: 2, md: 3 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Exercise Tracking
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab 
            label="Manual Entry" 
            icon={<Plus size={20} />} 
            iconPosition="start"
          />
          <Tab 
            label="Workout Templates" 
            icon={<Dumbbell size={20} />} 
            iconPosition="start"
          />
          <Tab 
            label="Weekly View" 
            icon={<Calendar size={20} />} 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      <TabPanel value={currentTab} index={0}>
        {recommendation && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Calendar size={16} />
              <Typography variant="body2">
                Today's recommended workout: <strong>{recommendation.name}</strong>
              </Typography>
            </Box>
            <Button
              size="small"
              variant="contained"
              onClick={() => handleUseTemplate(recommendation)}
              sx={{ mt: 1 }}
            >
              Use Today's Template
            </Button>
          </Alert>
        )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Plus size={24} color="#6366f1" />
                <Typography variant="h6">Log Exercise</Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Exercise Type</InputLabel>
                  <Select
                    value={exerciseType}
                    onChange={(e) => setExerciseType(e.target.value as any)}
                    label="Exercise Type"
                  >
                    <MenuItem value="walk">
                      <Box display="flex" alignItems="center" gap={1}>
                        <MapPin size={16} />
                        Walk
                      </Box>
                    </MenuItem>
                    <MenuItem value="gym-upper">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Dumbbell size={16} />
                        Gym - Upper Body
                      </Box>
                    </MenuItem>
                    <MenuItem value="gym-lower">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Dumbbell size={16} />
                        Gym - Lower Body
                      </Box>
                    </MenuItem>
                    <MenuItem value="cycling">
                      <Box display="flex" alignItems="center" gap={1}>
                        <MapPin size={16} />
                        Cycling
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Duration (minutes)"
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                  inputProps={{ min: "1" }}
                />

                {exerciseType === 'cycling' && (
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Distance (km)"
                        type="number"
                        value={cyclingDistance}
                        onChange={(e) => setCyclingDistance(e.target.value)}
                        inputProps={{ min: "0", step: "0.1" }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Box display="flex" alignItems="center" height="100%">
                        <Checkbox
                          checked={isCommute}
                          onChange={(e) => setIsCommute(e.target.checked)}
                        />
                        <Typography variant="body2">Bike commute</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                )}

                {exerciseType !== 'walk' && exerciseType !== 'cycling' && (
                  <Box sx={{ mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2">Gym Exercises</Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setDialogOpen(true)}
                        startIcon={<Plus size={16} />}
                      >
                        Add Exercise
                      </Button>
                    </Box>
                    
                    {gymExercises.map((exercise, index) => (
                      <Box key={index} display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {exercise.name} - {exercise.sets} sets × {exercise.reps}
                        </Typography>
                        <IconButton size="small" onClick={() => removeGymExercise(index)}>
                          <Trash2 size={16} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
                
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  sx={{ mb: 3 }}
                />
                
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={!duration || loading}
                  sx={{ py: 1.5 }}
                >
                  {loading ? 'Adding...' : 'Log Exercise'}
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Today's Activity
              </Typography>
              
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Timer size={20} color="#6366f1" />
                <Typography variant="h5" fontWeight="bold">
                  {totalDuration} min
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  total activity
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {Object.entries(stats).map(([type, stat]) => (
                  <Grid item xs={12} key={type}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                      <Chip
                        size="small"
                        label={getExerciseTypeLabel(type)}
                        sx={{ 
                          backgroundColor: getExerciseTypeColor(type),
                          color: 'white',
                          minWidth: '120px'
                        }}
                      />
                      <Typography variant="body2">
                        {stat.completed}/{stat.total} completed
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {stat.duration}min
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Today's Exercises
              </Typography>
              
              {todayExercises.length === 0 ? (
                <Typography color="text.secondary">
                  No exercises logged for today. Start by adding your first workout!
                </Typography>
              ) : (
                <List>
                  {todayExercises.map((exercise, index) => (
                    <React.Fragment key={exercise.id}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <Chip
                                size="small"
                                label={getExerciseTypeLabel(exercise.type)}
                                sx={{ 
                                  backgroundColor: getExerciseTypeColor(exercise.type),
                                  color: 'white'
                                }}
                              />
                              <Chip
                                size="small"
                                label={`${exercise.duration} min`}
                                variant="outlined"
                              />
                              {exercise.completed && (
                                <CheckCircle size={16} color="#22c55e" />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              {exercise.exercises && exercise.exercises.length > 0 && (
                                <Accordion>
                                  <AccordionSummary expandIcon={<ChevronDown />}>
                                    <Typography variant="body2">
                                      {exercise.exercises.length} exercises
                                    </Typography>
                                  </AccordionSummary>
                                  <AccordionDetails>
                                    {exercise.exercises.map((gymEx, gymIndex) => (
                                      <Box key={gymIndex} display="flex" alignItems="center" gap={1} mb={1}>
                                        <Checkbox
                                          checked={gymEx.completed}
                                          onChange={() => handleToggleGymExerciseCompleted(exercise, gymIndex)}
                                          size="small"
                                        />
                                        <Typography variant="body2">
                                          {gymEx.name} - {gymEx.sets} × {gymEx.reps}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </AccordionDetails>
                                </Accordion>
                              )}
                              {exercise.notes && (
                                <Typography variant="body2" color="text.secondary">
                                  {exercise.notes}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <Button
                          variant={exercise.completed ? "contained" : "outlined"}
                          color={exercise.completed ? "success" : "primary"}
                          onClick={() => handleToggleCompleted(exercise)}
                          size="small"
                        >
                          {exercise.completed ? 'Completed' : 'Mark Done'}
                        </Button>
                      </ListItem>
                      {index < todayExercises.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {recentExercises.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Exercises
                </Typography>
                
                <List>
                  {recentExercises.map((exercise, index) => (
                    <React.Fragment key={exercise.id}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip
                                size="small"
                                label={getExerciseTypeLabel(exercise.type)}
                                sx={{ 
                                  backgroundColor: getExerciseTypeColor(exercise.type),
                                  color: 'white'
                                }}
                              />
                              <Chip
                                size="small"
                                label={`${exercise.duration} min`}
                                variant="outlined"
                              />
                              {exercise.completed && (
                                <CheckCircle size={16} color="#22c55e" />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {format(new Date(exercise.date), 'MMM dd, yyyy')}
                              </Typography>
                              {exercise.exercises && (
                                <Typography variant="body2" color="text.secondary">
                                  {exercise.exercises.length} gym exercises
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < recentExercises.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}
        </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Workout Templates
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Pre-designed workouts based on your training schedule. Tuesday (Upper Body) and Thursday (Lower Body).
            </Typography>
          </Grid>

          <Grid item xs={12} sx={{ mb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Workout Templates</Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={resetTemplatesToDefault}
                startIcon={<Trash2 size={16} />}
              >
                Reset to Default
              </Button>
            </Box>
          </Grid>

          {workoutTemplates.map((template) => (
            <Grid item xs={12} md={6} key={template.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {template.name}
                      </Typography>
                      <Chip 
                        size="small" 
                        label={`${template.duration} min`} 
                        color="primary" 
                        sx={{ mr: 1 }} 
                      />
                      <Chip 
                        size="small" 
                        label={template.type === 'upper' ? 'Upper Body' : 'Lower Body & Core'} 
                        color="secondary" 
                      />
                    </Box>
                    <Box display="flex" gap={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleEditTemplate(template)}
                        startIcon={<Edit size={16} />}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleUseTemplate(template)}
                        startIcon={<Plus size={16} />}
                      >
                        Use Template
                      </Button>
                    </Box>
                  </Box>

                  <Typography variant="subtitle2" color="text.secondary" mb={1}>
                    🔥 Warmup:
                  </Typography>
                  <Typography variant="body2" mb={2}>
                    {template.warmup}
                  </Typography>

                  <Typography variant="subtitle2" color="text.secondary" mb={1}>
                    💪 Exercises:
                  </Typography>
                  <List dense>
                    {template.exercises.map((exercise, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemText
                          primary={
                            <Typography variant="body2">
                              <strong>{exercise.name}</strong> - {exercise.sets} sets × {exercise.reps}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Weekly Activity Goals
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <MapPin size={20} color="#22c55e" />
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          210 min/week
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Walking target (30-60 min/day)
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Dumbbell size={20} color="#ef4444" />
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          2 sessions/week
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Weight training (Tuesday & Thursday)
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Timer size={20} color="#f59e0b" />
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          25-40 min each
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Gym session duration
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Weekend Bonus:</strong> 60 min extra walking on Sat & Sun if eating out
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        <WeekNavigation 
          currentWeek={selectedWeek}
          onWeekChange={handleWeekChange}
          title="Weekly Exercise Overview"
        />
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Day</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Walk</strong></TableCell>
                <TableCell><strong>Gym - Upper</strong></TableCell>
                <TableCell><strong>Gym - Lower</strong></TableCell>
                <TableCell align="right"><strong>Total Duration</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {weeklyExerciseData.map((day) => {
                const walkExercises = day.exercises.filter(e => e.type === 'walk');
                const upperExercises = day.exercises.filter(e => e.type === 'gym-upper');
                const lowerExercises = day.exercises.filter(e => e.type === 'gym-lower');
                const totalDuration = day.exercises.reduce((sum, e) => sum + e.duration, 0);
                
                return (
                  <TableRow 
                    key={day.dateString}
                    sx={{ 
                      backgroundColor: day.isToday ? 'primary.main' : 'transparent',
                      '&:hover': { backgroundColor: day.isToday ? 'primary.main' : '#f8fafc' }
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography 
                          variant="body2" 
                          fontWeight={day.isToday ? 'bold' : 'normal'}
                          color={day.isToday ? 'white' : 'text.primary'}
                        >
                          {day.dayShort}
                        </Typography>
                        {day.isToday && (
                          <Chip size="small" label="Today" color="primary" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2"
                        color={day.isToday ? 'white' : 'text.secondary'}
                      >
                        {format(day.date, 'MMM dd')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {walkExercises.length > 0 ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2">
                            {walkExercises[0].duration} min
                          </Typography>
                          {walkExercises[0].completed && <CheckCircle size={14} color="#22c55e" />}
                          <MUIIconButton 
                            size="small" 
                            onClick={() => handleEditExercise(day.dateString, 'walk')}
                          >
                            <Edit size={14} />
                          </MUIIconButton>
                          {onDeleteExercise && (
                            <MUIIconButton 
                              size="small" 
                              onClick={() => handleDeleteExercise(walkExercises[0])}
                              color="error"
                            >
                              <Trash2 size={14} />
                            </MUIIconButton>
                          )}
                        </Box>
                      ) : (
                        <MUIIconButton 
                          size="small" 
                          onClick={() => handleEditExercise(day.dateString, 'walk')}
                          color="success"
                        >
                          <Plus size={14} />
                        </MUIIconButton>
                      )}
                    </TableCell>
                    <TableCell>
                      {upperExercises.length > 0 ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2">
                            {upperExercises[0].duration} min
                          </Typography>
                          {upperExercises[0].completed && <CheckCircle size={14} color="#22c55e" />}
                          <MUIIconButton 
                            size="small" 
                            onClick={() => handleEditExercise(day.dateString, 'gym-upper')}
                          >
                            <Edit size={14} />
                          </MUIIconButton>
                          {onDeleteExercise && (
                            <MUIIconButton 
                              size="small" 
                              onClick={() => handleDeleteExercise(upperExercises[0])}
                              color="error"
                            >
                              <Trash2 size={14} />
                            </MUIIconButton>
                          )}
                        </Box>
                      ) : (
                        <MUIIconButton 
                          size="small" 
                          onClick={() => handleEditExercise(day.dateString, 'gym-upper')}
                          color="success"
                        >
                          <Plus size={14} />
                        </MUIIconButton>
                      )}
                    </TableCell>
                    <TableCell>
                      {lowerExercises.length > 0 ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2">
                            {lowerExercises[0].duration} min
                          </Typography>
                          {lowerExercises[0].completed && <CheckCircle size={14} color="#22c55e" />}
                          <MUIIconButton 
                            size="small" 
                            onClick={() => handleEditExercise(day.dateString, 'gym-lower')}
                          >
                            <Edit size={14} />
                          </MUIIconButton>
                          {onDeleteExercise && (
                            <MUIIconButton 
                              size="small" 
                              onClick={() => handleDeleteExercise(lowerExercises[0])}
                              color="error"
                            >
                              <Trash2 size={14} />
                            </MUIIconButton>
                          )}
                        </Box>
                      ) : (
                        <MUIIconButton 
                          size="small" 
                          onClick={() => handleEditExercise(day.dateString, 'gym-lower')}
                          color="success"
                        >
                          <Plus size={14} />
                        </MUIIconButton>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        fontWeight="bold"
                        color={totalDuration > 0 ? 'success.main' : 'text.disabled'}
                      >
                        {totalDuration > 0 ? `${totalDuration} min` : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Gym Exercise Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Gym Exercise</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Exercise Name"
            value={newExerciseName}
            onChange={(e) => setNewExerciseName(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            placeholder="e.g., Bench Press, Squats, Pull-ups"
          />
          <TextField
            fullWidth
            label="Sets"
            type="number"
            value={newExerciseSets}
            onChange={(e) => setNewExerciseSets(e.target.value)}
            sx={{ mb: 2 }}
            inputProps={{ min: "1" }}
          />
          <TextField
            fullWidth
            label="Reps"
            value={newExerciseReps}
            onChange={(e) => setNewExerciseReps(e.target.value)}
            placeholder="e.g., 12, 8-10, 30 sec"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={editingDay ? handleAddEditExercise : addGymExercise} 
            variant="contained"
            disabled={!newExerciseName || !newExerciseSets || !newExerciseReps}
          >
            Add Exercise
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Template Edit Dialog */}
      <Dialog open={!!editingTemplate} onClose={handleCancelEditTemplate} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit Template: {editingTemplateData?.name}
        </DialogTitle>
        <DialogContent>
          {editingTemplateData && (
            <Box>
              <TextField
                fullWidth
                label="Template Name"
                value={editingTemplateData.name}
                onChange={(e) => updateTemplateField('name', e.target.value)}
                sx={{ mb: 2, mt: 1 }}
              />
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Duration (minutes)"
                    type="number"
                    value={editingTemplateData.duration}
                    onChange={(e) => updateTemplateField('duration', parseInt(e.target.value))}
                    inputProps={{ min: "1" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Workout Type</InputLabel>
                    <Select
                      value={editingTemplateData.type}
                      label="Workout Type"
                      onChange={(e) => updateTemplateField('type', e.target.value)}
                    >
                      <MenuItem value="upper">Upper Body</MenuItem>
                      <MenuItem value="lower">Lower Body & Core</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              
              <TextField
                fullWidth
                label="Warmup"
                multiline
                rows={2}
                value={editingTemplateData.warmup}
                onChange={(e) => updateTemplateField('warmup', e.target.value)}
                sx={{ mb: 3 }}
                placeholder="Describe the warmup routine..."
              />
              
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Exercises</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={addExerciseToTemplate}
                  startIcon={<Plus size={16} />}
                >
                  Add Exercise
                </Button>
              </Box>
              
              {editingTemplateData.exercises.map((exercise, index) => (
                <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ pb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={5}>
                        <TextField
                          fullWidth
                          label="Exercise Name"
                          value={exercise.name}
                          onChange={(e) => updateTemplateExercise(index, 'name', e.target.value)}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <TextField
                          fullWidth
                          label="Sets"
                          type="number"
                          value={exercise.sets}
                          onChange={(e) => updateTemplateExercise(index, 'sets', parseInt(e.target.value))}
                          inputProps={{ min: "1" }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          fullWidth
                          label="Reps"
                          value={exercise.reps}
                          onChange={(e) => updateTemplateExercise(index, 'reps', e.target.value)}
                          placeholder="e.g., 10-12, 30 sec"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <IconButton
                          onClick={() => removeExerciseFromTemplate(index)}
                          color="error"
                          size="small"
                        >
                          <Trash2 size={18} />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
              
              {editingTemplateData.exercises.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No exercises added yet. Click "Add Exercise" to start building your template.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEditTemplate}>
            <X size={16} style={{ marginRight: 4 }} />
            Cancel
          </Button>
          <Button 
            onClick={handleSaveTemplate} 
            variant="contained"
            disabled={!editingTemplateData?.name || !editingTemplateData?.warmup}
          >
            <Save size={16} style={{ marginRight: 4 }} />
            Save Template
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit Exercise Dialog */}
      <Dialog open={!!editingDay} onClose={handleCancelEdit} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDay && editingDay.dateString && (
            `${editDuration ? 'Edit' : 'Add'} ${getExerciseTypeLabel(editingDay.type)} - ${format(new Date(editingDay.dateString + 'T12:00:00'), 'EEEE, MMM dd')}`
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Duration (minutes)"
            type="number"
            value={editDuration}
            onChange={(e) => setEditDuration(e.target.value)}
            required
            sx={{ mb: 2, mt: 1 }}
            inputProps={{ min: "1" }}
            autoFocus
          />
          
          {editingDay && editingDay.type !== 'walk' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Gym Exercises
              </Typography>
              
              <Box display="flex" gap={1} mb={2}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setDialogOpen(true)}
                  startIcon={<Plus size={16} />}
                >
                  Add Exercise
                </Button>
              </Box>
              
              {editExercises.map((exercise, index) => (
                <Box key={index} display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {exercise.name} - {exercise.sets} sets × {exercise.reps}
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => {
                      setEditExercises(editExercises.filter((_, i) => i !== index));
                    }}
                  >
                    <Trash2 size={16} />
                  </IconButton>
                </Box>
              ))}
              
              {editExercises.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No exercises added yet. Click "Add Exercise" to start building your workout.
                </Typography>
              )}
            </Box>
          )}
          
          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={2}
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} startIcon={<X size={16} />}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveExercise} 
            variant="contained"
            disabled={!editDuration || quickAddLoading}
            startIcon={<Save size={16} />}
          >
            {quickAddLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};